import { useState } from 'react';
import {
  Button,
  Heading,
  Card,
  CardContent,
  Paragraph,
} from '@/components/atoms';
import { ArrowRightIcon, MapIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useProductTour } from '@/components/organisms';
import {
  EmptyState,
  AskSellytModal,
  ArticleModal,
  SuggestedArticles,
  SearchSection,
} from './components';
import { useArticles } from '@/api';
import { SupportChatWidget } from '@/components/organisms';
import type { Article } from '@/types';

export const SupportPage = () => {
  const { restartTour } = useProductTour();
  const [askSellytOpen, setAskSellytOpen] = useState(false);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: articlesData, isLoading: isLoadingArticles } =
    useArticles(searchQuery);
  const articles = articlesData?.data ?? [];

  const handleAskSellytClick = () => {
    setAskSellytOpen(true);
  };

  const handleArticleClick = (article: Article) => {
    setSelectedArticle(article);
    setArticleModalOpen(true);
  };

  const showEmptyState =
    searchQuery.trim() !== '' && !isLoadingArticles && articles.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <Card className={cn('p-0 overflow-hidden')}>
        {/* Welcome Banner */}
        <div className={cn('bg-primary text-white py-20 px-12 rounded-t-lg')}>
          <Heading level={1} className="font-bold text-white">
            Welcome to Sellyt support
          </Heading>
        </div>

        <CardContent className="py-6">
          {/* Ask Sellyt Section */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <Button
              onClick={handleAskSellytClick}
              className="flex-1 max-w-md bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white"
            >
              Ask Sellyt
              <ArrowRightIcon className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={restartTour}
              className="max-w-md shrink-0"
            >
              <MapIcon className="w-4 h-4 mr-2" />
              Take the dashboard tour
            </Button>
          </div>
          <Paragraph size="sm" className="text-muted-foreground max-w-2xl">
            New to Sellyt? Replay the guided walkthrough for KYB, bank setup,
            store, products, orders, shipping, conversations, and billing.
          </Paragraph>

          {/* Articles (or Empty) + Search always visible */}
          <div className="flex flex-col-reverse lg:grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Left: Articles or Empty State */}
            {showEmptyState ? (
              <div className="lg:col-span-2">
                <EmptyState onAskSellyt={handleAskSellytClick} />
              </div>
            ) : (
              <SuggestedArticles
                searchQuery={searchQuery}
                onArticleClick={handleArticleClick}
              />
            )}

            {/* Right: Search Section (always visible) */}
            <SearchSection
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>
        </CardContent>
      </Card>

      {/* Ask Sellyt Modal */}
      <AskSellytModal
        isOpen={askSellytOpen}
        onClose={() => setAskSellytOpen(false)}
      />

      {/* Article Modal */}
      <ArticleModal
        isOpen={articleModalOpen}
        onClose={() => setArticleModalOpen(false)}
        article={selectedArticle}
      />

      <SupportChatWidget onRequestHumanSupport={handleAskSellytClick} />
    </div>
  );
};
